import { useToken } from '@ori/auth';
import { useCookieStoreInfo } from '@ori/personal-beauty-store';
import { useUserSegmentationData } from '@ori/presentation-http';
import { isSSR } from '@ori/presentation-utils';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

import type { BannersCarouselItem } from '../../components/TopAreaBanner/components/BannersCarousel/types';
import { getTopAreaBannerType } from '../../components/TopAreaBanner/utils';
import { useGetTopAreaBanners, shouldSkipTopAreaBannersFetch } from '../../hooks/useGetTopAreaBanners';
import type { VipAccessResponse } from '../../models';
import { useAppConfig } from '../configurationContext';
import { useConsultant } from '../consultantContext';
import { isLoading, isOrderOnBehalfVisible, isTopAreaBannerVisible } from './utils';

export interface TopAreaBannerContext {
  bannersData: BannersCarouselItem[] | undefined;
  isAnonymousWithStore: boolean;
  isOrderOnBehalfVisible: boolean;
  loading: boolean;
  orderOnBehalfData: VipAccessResponse | undefined;
  visible: boolean;
}

const defaultTopAreaBannerContextValue: TopAreaBannerContext = {
  bannersData: undefined,
  isAnonymousWithStore: false,
  isOrderOnBehalfVisible: false,
  loading: true,
  orderOnBehalfData: undefined,
  visible: false,
};

export const topAreaBannerContext = createContext<TopAreaBannerContext>(defaultTopAreaBannerContextValue);

const { Provider } = topAreaBannerContext;

export const TopAreaBannerProvider: FC<PropsWithChildren> = ({ children }) => {
  const { owner: store } = useCookieStoreInfo();
  const { loadingVipAccess, vipAccess: orderOnBehalfData } = useConsultant();
  const { enableTopAreaBanner } = useAppConfig();
  const { isAnonymousUser } = useToken();
  const { loading: loadingSegmentationData } = useUserSegmentationData();
  const isAnonymousWithStore = useMemo(() => isAnonymousUser && !!store, [isAnonymousUser, store]);
  const shouldGetBannerType = !isSSR() && enableTopAreaBanner;
  const bannerType = useMemo(
    () => (shouldGetBannerType ? getTopAreaBannerType(window.location.pathname, isAnonymousWithStore) : null),
    [isAnonymousWithStore, shouldGetBannerType],
  );
  /**
   * VipAccessData can be defined but can arrive with empty key properties.
   */
  const orderOnBehalfVisible = useMemo(() => isOrderOnBehalfVisible(orderOnBehalfData), [orderOnBehalfData]);
  const { bannersData, loading: isLoadingTopBanner } = useGetTopAreaBanners({
    bannerType,
    disableFetching: shouldSkipTopAreaBannersFetch({
      enableTopAreaBanner,
      loadingSegmentationData,
      orderOnBehalfVisible,
      bannerType,
    }),
  });
  const topBannerVisible = useMemo(() => isTopAreaBannerVisible(bannersData), [bannersData]);

  const visible = orderOnBehalfVisible || Boolean(enableTopAreaBanner);

  return (
    <Provider
      value={useMemo<TopAreaBannerContext>(
        () => ({
          bannersData,
          isAnonymousWithStore,
          isOrderOnBehalfVisible: orderOnBehalfVisible,
          loading: isLoading({
            isLoadingTopBanner,
            topBannerVisible,
            isAnonymousWithStore,
            loadingVipAccess,
            orderOnBehalfVisible,
          }),
          orderOnBehalfData,
          visible,
        }),
        [
          bannersData,
          isAnonymousWithStore,
          isLoadingTopBanner,
          loadingVipAccess,
          orderOnBehalfData,
          orderOnBehalfVisible,
          topBannerVisible,
          visible,
        ],
      )}
    >
      {children}
    </Provider>
  );
};

export const useTopAreaBanner = () => useContext(topAreaBannerContext);
